import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  type OnDestroy,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OdlInputEvent } from '@odilo/ui-components';
import { debugLog } from '../../../helpers/debugLog';
import type { Competency } from '../../../models/metadata-response';
import type { QuizAnswerResponse, QuizDraft } from '../../../models/quiz';
import { MetadataService } from '../../../services/metadata/metadata';
import { OfflineSessionService } from '../../../services/offline/offline-session';
import { PronunciationSpeechService } from '../../../services/pronunciation-speech/pronunciation-speech';
import { QuizService } from '../../../services/quiz/quiz';
import { ToastService } from '../../../services/toast/toast';
import { VoiceRecorderService } from '../../../services/voice-recorder/voice-recorder';
import { AudioPlayerComponent } from '../../audio-player/audio-player';
import { GuidedTourComponent } from '../../guided-tour/guided-tour';
import { GuidedTourAnchorDirective } from '../../guided-tour/guided-tour-anchor';
import type { GuidedTourStep } from '../../guided-tour/guided-tour.model';
import { CompetencyPickerComponent } from '../competency-picker/competency-picker';
import { PronunciationFeedbackComponent } from '../pronunciation-feedback/pronunciation-feedback';
import { PronunciationScoresComponent } from '../pronunciation-feedback/pronunciation-scores/pronunciation-scores';
import { SentenceSpeechComponent } from '../sentence-speech/sentence-speech';

type VoicePhase = 'answering' | 'submitting' | 'pending' | 'score' | 'watching';

const TICK_MS = 1000;
const SECONDS_PER_MINUTE = 60;
const SECONDS_STRING_LENGTH = 2;
const MIN_GRADE = 0;
const MAX_GRADE = 10;
const DECIMAL_BASE = 10;
const GRADE_DECIMAL_PLACES = 3;
const GRADE_ROUNDING_FACTOR = DECIMAL_BASE ** GRADE_DECIMAL_PLACES;
const PRONUNCIATION_PRACTICE_TOUR_ID = 'pronunciation-practice';
/** Before answering: the play button and the turtle next to the sentence, then the record button. */
const PRONUNCIATION_PRACTICE_TOUR_STEPS: GuidedTourStep[] = [
  {
    anchor: 'sentence',
    title: 'QUESTIONS.PRONUNCIATION.TOUR.SENTENCE_TITLE',
    text: 'QUESTIONS.PRONUNCIATION.TOUR.SENTENCE_TEXT',
  },
  {
    anchor: 'slow',
    title: 'QUESTIONS.PRONUNCIATION.TOUR.SLOW_TITLE',
    text: 'QUESTIONS.PRONUNCIATION.TOUR.SLOW_TEXT',
  },
  {
    anchor: 'record',
    title: 'QUESTIONS.START_RECORDING',
    text: 'QUESTIONS.PRONUNCIATION.TOUR.RECORD_TEXT',
  },
];
const PRONUNCIATION_TOUR_ID = 'pronunciation-feedback';
/** After answering, top to bottom as the feedback shows them: the words, the recording and the scores. */
const PRONUNCIATION_TOUR_STEPS: GuidedTourStep[] = [
  {
    anchor: 'word',
    title: 'QUESTIONS.PRONUNCIATION.TOUR.WORD_TITLE',
    text: 'QUESTIONS.PRONUNCIATION.TOUR.WORD_TEXT',
  },
  {
    anchor: 'recording',
    title: 'QUESTIONS.PRONUNCIATION.TOUR.RECORDING_TITLE',
    text: 'QUESTIONS.PRONUNCIATION.TOUR.RECORDING_TEXT',
    optional: true,
  },
  {
    anchor: 'scores',
    title: 'QUESTIONS.PRONUNCIATION.TOUR.SCORES_TITLE',
    text: 'QUESTIONS.PRONUNCIATION.TOUR.SCORES_TEXT',
    optional: true,
  },
];

@Component({
  selector: 'nr-voice-recording-dialog',
  imports: [
    TranslatePipe,
    AudioPlayerComponent,
    NgTemplateOutlet,
    PronunciationFeedbackComponent,
    PronunciationScoresComponent,
    GuidedTourComponent,
    GuidedTourAnchorDirective,
    SentenceSpeechComponent,
    CompetencyPickerComponent,
  ],
  templateUrl: './voice-recording-dialog.html',
  styleUrl: './voice-recording-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VoiceRecordingDialogComponent implements OnDestroy {
  private readonly quizService = inject(QuizService);
  private readonly metadataService = inject(MetadataService);
  private readonly offlineSession = inject(OfflineSessionService);
  private readonly pronunciationSpeech = inject(PronunciationSpeechService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly voiceRecorder = inject(VoiceRecorderService);

  private recordingStartedAt = 0;
  private timerHandle?: ReturnType<typeof setInterval>;
  private recordedObjectUrl?: string;

  readonly draft = this.quizService.quizDraft;

  readonly isTeacher = this.metadataService.isTeacher;

  readonly canManageQuestions = this.metadataService.canManageQuestions;

  readonly isTeacherEditing = computed(
    () => this.quizService.quizMode() === 'voice' && Boolean(this.draft()),
  );

  readonly questionText = computed(() => this.draft()?.questions[0]?.text ?? '');

  readonly pronunciationAssessmentEnabled = computed(() =>
    Boolean(this.draft()?.pronunciationAssessmentEnabled),
  );

  readonly draftHasAnswers = computed(() => Boolean(this.draft()?.hasAnswers));

  readonly isReadOnly = computed(() => this.draftHasAnswers() || !this.canManageQuestions());

  readonly canUsePronunciationAssessment = this.metadataService.canUsePronunciationAssessment;

  readonly voiceRecordingMaxSeconds = this.metadataService.voiceRecordingMaxSeconds;

  readonly competencies = this.metadataService.competencies;

  readonly settingsExpanded = signal(true);

  readonly selectedCompetencies = computed(() => this.draft()?.competencies ?? []);

  readonly confirmDelete = signal(false);

  readonly saving = signal(false);

  readonly quiz = this.quizService.quizToAnswer;

  readonly myAnswer = computed(() => {
    const quizId = this.quiz()?.id;
    if (!quizId) return undefined;
    return this.quizService.quizzes().find((item) => item.quiz.id === quizId)?.myAnswer;
  });

  readonly phase = signal<VoicePhase>(this.initialPhase());

  readonly isRecording = signal(false);

  readonly remainingSeconds = signal(0);

  readonly recordedBlob = signal<Blob | undefined>(undefined);

  readonly recordedAudioUrl = signal<string | undefined>(undefined);

  readonly recordedDurationMs = signal<number | undefined>(undefined);

  readonly hasRecording = computed(() => Boolean(this.recordedBlob()));

  readonly isSubmitting = signal(false);

  readonly timeLeftDisplay = computed(() =>
    VoiceRecordingDialogComponent.formatMinutesSeconds(this.remainingSeconds()),
  );

  readonly gradeOutOfTen = computed(() => {
    const score = this.myAnswer()?.score;
    if (score == null) return undefined;
    const clampedScore = Math.min(MAX_GRADE, Math.max(MIN_GRADE, score));
    return VoiceRecordingDialogComponent.roundGrade(clampedScore);
  });

  readonly isPendingManualReview = computed(() => Boolean(this.myAnswer()?.pendingManualReview));

  /** Answered offline: graded by the server once it is synced. */
  readonly isPendingSync = computed(() => Boolean(this.myAnswer()?.pendingSync));

  readonly hasScore = computed(() => this.myAnswer()?.score != null);

  /** Offline only the recording made in this visit plays: the stored one needs connection. */
  readonly answeredAudioUrl = computed(() =>
    this.offlineSession.isOffline()
      ? this.recordedAudioUrl()
      : (this.myAnswer()?.audioUrl ?? this.recordedAudioUrl()),
  );

  readonly answeredDurationMs = computed(
    () => this.myAnswer()?.audioDurationMs ?? this.recordedDurationMs(),
  );

  readonly pronunciationAssessment = computed(() => this.myAnswer()?.pronunciationAssessment);

  /** Shown to students the first time they can listen to their feedback; it waits for the play button. */
  readonly practiceTourId = PRONUNCIATION_PRACTICE_TOUR_ID;

  readonly practiceTourSteps = PRONUNCIATION_PRACTICE_TOUR_STEPS;

  readonly pronunciationTourId = PRONUNCIATION_TOUR_ID;

  readonly pronunciationTourSteps = PRONUNCIATION_TOUR_STEPS;

  ngOnDestroy(): void {
    this.clearTimer();
    this.voiceRecorder.cancel();
    if (this.recordedObjectUrl) URL.revokeObjectURL(this.recordedObjectUrl);
  }

  togglePronunciationAssessment(event: OdlInputEvent<boolean>): void {
    this.updateDraft((draft) => ({ ...draft, pronunciationAssessmentEnabled: event.detail.value }));
  }

  toggleSettingsExpanded(): void {
    this.settingsExpanded.update((expanded) => !expanded);
  }

  updateCompetencies(competencies: Competency[]): void {
    this.updateDraft((draft) => ({ ...draft, competencies }));
  }

  async save(): Promise<void> {
    if (this.saving() || this.isReadOnly()) return;
    this.saving.set(true);
    try {
      await this.quizService.saveQuizDraft();
    } finally {
      this.saving.set(false);
    }
  }

  cancel(): void {
    if (this.saving()) return;
    this.quizService.cancelQuizDraft();
  }

  async confirmDeleteVoice(): Promise<void> {
    const draft = this.draft();
    this.confirmDelete.set(false);
    if (!draft?.id) return;
    await this.quizService.deleteQuiz(draft.id, draft.relId);
  }

  async startRecording(): Promise<void> {
    if (this.isRecording() || this.isSubmitting()) return;
    this.pronunciationSpeech.stop();
    try {
      await this.voiceRecorder.start();
    } catch (error) {
      debugLog('Error accessing microphone:', error);
      this.toastService.warning(
        String(this.translateService.instant('QUESTIONS.MIC_PERMISSION_DENIED')),
      );
      return;
    }
    this.reRecord();
    this.recordingStartedAt = Date.now();
    this.remainingSeconds.set(this.voiceRecordingMaxSeconds());
    this.isRecording.set(true);
    this.timerHandle = setInterval(() => {
      this.tick();
    }, TICK_MS);
  }

  async stopRecording(): Promise<void> {
    if (!this.isRecording()) return;
    this.clearTimer();
    const durationMs = Date.now() - this.recordingStartedAt;
    const recorded = await this.voiceRecorder.stop();
    this.isRecording.set(false);
    if (!recorded) return;
    this.recordedDurationMs.set(durationMs);
    this.recordedBlob.set(recorded);
    this.recordedObjectUrl = URL.createObjectURL(recorded);
    this.recordedAudioUrl.set(this.recordedObjectUrl);
  }

  reRecord(): void {
    if (this.recordedObjectUrl) URL.revokeObjectURL(this.recordedObjectUrl);
    this.recordedObjectUrl = undefined;
    this.recordedBlob.set(undefined);
    this.recordedAudioUrl.set(undefined);
    this.recordedDurationMs.set(undefined);
  }

  async submit(): Promise<void> {
    const quiz = this.quiz();
    const blob = this.recordedBlob();
    if (!quiz || !blob || this.isSubmitting()) return;
    this.isSubmitting.set(true);
    this.phase.set('submitting');
    try {
      const answer = await this.quizService.submitVoiceAnswer(
        quiz,
        blob,
        this.recordedDurationMs() ?? 0,
      );
      this.phase.set(answer ? VoiceRecordingDialogComponent.answeredPhase(answer) : 'answering');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  seeAnswer(): void {
    this.phase.set('watching');
  }

  back(): void {
    this.phase.set('score');
  }

  close(): void {
    this.clearTimer();
    this.voiceRecorder.cancel();
    this.pronunciationSpeech.stop();
    this.quizService.closeQuizAnswer();
  }

  private tick(): void {
    const remaining = this.remainingSeconds() - 1;
    this.remainingSeconds.set(Math.max(0, remaining));
    if (remaining <= 0) this.stopRecording();
  }

  private clearTimer(): void {
    if (this.timerHandle) clearInterval(this.timerHandle);
    this.timerHandle = undefined;
  }

  private updateDraft(updater: (draft: QuizDraft) => QuizDraft): void {
    const current = this.draft();
    if (!current) return;
    this.quizService.quizDraft.set(updater(current));
  }

  private initialPhase(): VoicePhase {
    const answer = this.myAnswer();
    return answer ? VoiceRecordingDialogComponent.answeredPhase(answer) : 'answering';
  }

  /** Without a grade yet (pending review or sync) there is no score to show. */
  private static answeredPhase(answer: QuizAnswerResponse): VoicePhase {
    return answer.pendingManualReview || answer.pendingSync ? 'pending' : 'score';
  }

  private static formatMinutesSeconds(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
    const seconds = totalSeconds % SECONDS_PER_MINUTE;
    return `${minutes.toString().padStart(SECONDS_STRING_LENGTH, '0')}:${seconds.toString().padStart(SECONDS_STRING_LENGTH, '0')}`;
  }

  private static roundGrade(value: number): number {
    return Math.round(value * GRADE_ROUNDING_FACTOR) / GRADE_ROUNDING_FACTOR;
  }
}
